<template>
  <div class="container-fluid">
    <div class="row mb-3 align-items-center">
      <div class="col">
        <button class="btn btn-link pl-0" @click="goBack">← Volver</button>
        <h4 class="mb-1">{{ isEdit ? 'Editar bonificación' : 'Nueva bonificación' }}</h4>
        <p class="text-muted mb-0">
          Ejemplo: “Inicio” = 10 viajes del 1 al 10 de agosto → bono de $500 al corte
          (depósito admin = tarjeta + bono − deuda; el efectivo no entra al depósito).
        </p>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div v-if="loading" class="text-center py-5 text-muted">Cargando…</div>

    <div v-else class="card">
      <div class="card-body">
        <form @submit.prevent="submit">
          <div class="row">
            <div class="col-md-6 form-group">
              <label>Título</label>
              <input v-model.trim="form.title" class="form-control" required maxlength="120" />
            </div>
            <div class="col-md-6 form-group">
              <label>Descripción (opcional)</label>
              <input v-model.trim="form.description" class="form-control" maxlength="255" />
            </div>
            <div class="col-md-4 form-group">
              <label>Viajes a completar</label>
              <input
                v-model.number="form.targetTrips"
                type="number"
                min="1"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-4 form-group">
              <label>Monto del bono (CLP)</label>
              <input
                v-model.number="form.rewardAmount"
                type="number"
                min="0"
                step="1"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-4 form-group">
              <label>Estado</label>
              <select v-model="form.isActive" class="form-control">
                <option :value="true">Activo</option>
                <option :value="false">Inactivo</option>
              </select>
            </div>
            <div class="col-md-3 form-group">
              <label>Fecha inicio</label>
              <input v-model="form.startDate" type="date" class="form-control" required />
            </div>
            <div class="col-md-3 form-group">
              <label>Hora inicio</label>
              <input v-model="form.startTime" type="time" class="form-control" required />
            </div>
            <div class="col-md-3 form-group">
              <label>Fecha fin (límite)</label>
              <input v-model="form.endDate" type="date" class="form-control" required />
            </div>
            <div class="col-md-3 form-group">
              <label>Hora fin</label>
              <input v-model="form.endTime" type="time" class="form-control" required />
            </div>
          </div>

          <p class="small text-muted mb-3">
            Solo cuentan viajes finalizados entre la fecha/hora de inicio y fin. Si completa la meta
            a tiempo, el bono entra al depósito del corte (tarjeta + bono − deuda). Si no, no recibe
            nada.
          </p>

          <div class="text-right">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="goBack">
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Guardando…' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createDriverBono,
  fetchDriverBono,
  updateDriverBono,
} from '@/services/driverBonosApi';

function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toTimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Combina fecha + hora local → ISO (horario del navegador / Chile en panel). */
function dateTimeToIso(dateStr, timeStr) {
  const [y, m, d] = String(dateStr || '')
    .split('-')
    .map(Number);
  const [hh, mm] = String(timeStr || '00:00')
    .split(':')
    .map((v) => Number(v) || 0);
  const dt = new Date(y, (m || 1) - 1, d || 1, hh, mm, 0, 0);
  if (Number.isNaN(dt.getTime())) {
    throw new Error('Fecha u hora inválida');
  }
  return dt.toISOString();
}

export default {
  name: 'BonoConductorFormPage',
  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: '',
      form: {
        title: '',
        description: '',
        targetTrips: 10,
        rewardAmount: 500,
        startDate: '',
        startTime: '00:00',
        endDate: '',
        endTime: '23:59',
        isActive: true,
      },
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    },
  },
  created() {
    if (this.isEdit) this.loadOne();
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'bono-conductor' });
    },
    async loadOne() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const row = await fetchDriverBono(this.$route.params.id);
        this.form = {
          title: row.title || '',
          description: row.description || '',
          targetTrips: row.targetTrips,
          rewardAmount: row.rewardAmount,
          startDate: toDateInput(row.startsAt),
          startTime: toTimeInput(row.startsAt) || '00:00',
          endDate: toDateInput(row.endsAt),
          endTime: toTimeInput(row.endsAt) || '23:59',
          isActive: row.isActive !== false,
        };
      } catch (e) {
        this.errorMessage = e.message || 'Error al cargar';
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.errorMessage = '';
      this.saving = true;
      try {
        const startsAt = dateTimeToIso(this.form.startDate, this.form.startTime);
        const endsAt = dateTimeToIso(this.form.endDate, this.form.endTime);
        if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
          throw new Error('La fecha/hora fin debe ser posterior a la de inicio');
        }
        const body = {
          title: this.form.title.trim(),
          description: this.form.description?.trim() || undefined,
          targetTrips: Number(this.form.targetTrips),
          rewardAmount: Number(this.form.rewardAmount),
          startsAt,
          endsAt,
          isActive: this.form.isActive !== false,
        };
        if (this.isEdit) {
          await updateDriverBono(this.$route.params.id, body);
        } else {
          await createDriverBono(body);
        }
        this.$router.push({ name: 'bono-conductor', query: { saved: '1' } });
      } catch (e) {
        this.errorMessage = e.message || 'No se pudo guardar';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
